import React, { useState, useEffect, useMemo } from "react";
import { getData, postData, putData, deleteData } from "../../services/api.service";
import { toastSuccess, toastError } from "../common/Toast";
import { logError, logInfo } from "../../utils/errorLogger";
import {
  FaCube,
  FaPlus,
  FaEdit,
  FaTrash,
  FaSave,
  FaExclamationTriangle
} from "react-icons/fa";

function SkuMainsManager() {
  const [skus, setSkus] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingSku, setEditingSku] = useState(null);
  const [deletingSku, setDeletingSku] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [formData, setFormData] = useState({
    sku_category: '',
    sku_description: '',
    manufacturing_time: '',
    active: 1
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      logInfo('Cargando datos de SKUs y categorías');
      
      const [skuResponse, categoryResponse] = await Promise.all([
        getData('/api/admin/get_sku_mains', false),  // No mostrar alert automático
        getData('/api/admin/get_sku_categories', false)
      ]);
      
      // Validar que las respuestas sean válidas
      if (skuResponse?.data?.skuMains) {
        setSkus(skuResponse.data.skuMains);
        logInfo(`${skuResponse.data.skuMains.length} SKUs cargados`);
      } else {
        console.warn('Respuesta de SKUs inválida:', skuResponse);
        logError(new Error('Respuesta de SKUs inválida'), { response: skuResponse });
        setSkus([]);
      }
      
      if (categoryResponse?.data?.skuCategories) {
        setCategories(categoryResponse.data.skuCategories);
        logInfo(`${categoryResponse.data.skuCategories.length} categorías cargadas`);
      } else {
        console.warn('Respuesta de categorías inválida:', categoryResponse);
        logError(new Error('Respuesta de categorías inválida'), { response: categoryResponse });
        setCategories([]);
      }
    } catch (error) {
      console.error('Error cargando datos:', error);
      logError(error, { 
        context: 'loadData',
        action: 'Carga inicial de SKUs y categorías'
      });
      toastError('Error al cargar los datos de SKUs');
      // Asegurar que siempre haya un array válido
      setSkus([]);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  // Función optimizada para recargar solo SKUs (no categorías)
  const loadSkusOnly = async () => {
    try {
      logInfo('Recargando solo SKUs');
      const skuResponse = await getData('/api/admin/get_sku_mains', false);
      
      if (skuResponse?.data?.skuMains) {
        setSkus(skuResponse.data.skuMains);
        logInfo(`${skuResponse.data.skuMains.length} SKUs recargados`);
      } else {
        console.warn('Respuesta de SKUs inválida:', skuResponse);
        setSkus([]);
      }
    } catch (error) {
      console.error('Error recargando SKUs:', error);
      logError(error, { context: 'loadSkusOnly' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      logInfo(editingSku ? 'Actualizando SKU' : 'Creando nuevo SKU', { formData });
      
      const dataToSend = {
        data: {
          sku_description: formData.sku_description,
          sku_category: parseInt(formData.sku_category),
          manufacturing_time: parseInt(formData.manufacturing_time) || 0,
          active: formData.active,
          user: 1 // TODO: Obtener del contexto de usuario actual
        }
      };

      if (editingSku) {
        dataToSend.data.id = editingSku.id;
        dataToSend.data.sku = editingSku.sku; // Mantener el SKU existente
        
        const response = await putData('/api/admin/update_sku_main', dataToSend);
        
        // Verificar si la respuesta es un error
        if (response.response && response.response.status >= 400) {
          logError(new Error('Error al actualizar SKU'), { 
            response: response.response, 
            data: dataToSend 
          });
          throw new Error(response.response.data?.msg || 'Error al actualizar');
        }
        
        // Actualización optimista: actualizar el item en la lista local sin recargar todo
        setSkus(prevSkus => prevSkus.map(sku => 
          sku.id === editingSku.id 
            ? {
                ...sku,
                sku_description: formData.sku_description,
                sku_category: parseInt(formData.sku_category),
                category_name: categories.find(c => c.id === parseInt(formData.sku_category))?.category_name || sku.category_name,
                manufacturing_time: parseInt(formData.manufacturing_time) || 0,
                active: formData.active
              }
            : sku
        ));
        
        logInfo('SKU actualizado exitosamente', { id: editingSku.id });
        toastSuccess(`SKU "${formData.sku_description}" actualizado exitosamente`);
      } else {
        const response = await postData('/api/admin/create_sku_main', dataToSend);
        
        // Verificar si la respuesta es un error
        if (response.response && response.response.status >= 400) {
          logError(new Error('Error al crear SKU'), { 
            response: response.response, 
            data: dataToSend 
          });
          throw new Error(response.response.data?.msg || 'Error al crear');
        }
        
        logInfo('SKU creado exitosamente', { response: response.data });
        
        // ACTUALIZACIÓN OPTIMISTA: Agregar el nuevo SKU inmediatamente al estado
        if (response.data?.sku) {
          const newSku = {
            id: response.data.sku.id || response.data.id,
            sku: response.data.sku.sku || response.data.sku,
            sku_description: formData.sku_description,
            sku_category: parseInt(formData.sku_category),
            category_name: categories.find(c => c.id === parseInt(formData.sku_category))?.category_name || '',
            manufacturing_time: parseInt(formData.manufacturing_time) || 0,
            active: formData.active
          };
          
          // Agregar al inicio de la lista para que sea visible inmediatamente
          setSkus(prevSkus => [newSku, ...prevSkus]);
          logInfo('SKU agregado optimísticamente a la UI', { newSku });
        }
        
        toastSuccess(`SKU "${formData.sku_description}" creado exitosamente`);
        
        // Cerrar modal y resetear inmediatamente
        setShowModal(false);
        resetForm();
        
        // Recargar en background para sincronizar con servidor (sin bloquear UI)
        loadSkusOnly().catch(err => {
          logError(err, { context: 'handleSubmit->loadSkusOnly' });
        });
        
        return; // Salir aquí para no cerrar modal dos veces
      }

      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error('Error guardando SKU:', error);
      logError(error, { 
        context: 'handleSubmit',
        action: editingSku ? 'Actualizar SKU' : 'Crear SKU',
        formData
      });
      toastError(error.message || (editingSku ? 'Error al actualizar el SKU' : 'Error al crear el SKU'));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingSku(null);
    setFormData({
      sku_category: '',
      sku_description: '',
      manufacturing_time: '',
      active: 1
    });
  };

  const handleEdit = (sku) => {
    setEditingSku(sku);
    setFormData({
      sku_category: sku.sku_category || '',
      sku_description: sku.sku_description || '',
      manufacturing_time: sku.manufacturing_time || '',
      active: sku.active !== undefined ? sku.active : 1
    });
    setShowModal(true);
  };

  const handleNew = () => {
    setEditingSku(null);
    setFormData({
      sku_category: '',
      sku_description: '',
      manufacturing_time: '',
      active: 1
    });
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleDelete = (sku) => {
    setDeletingSku(sku);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deletingSku) return;
    
    try {
      setLoading(true);
      
      // Usar PUT para marcar como inactivo en lugar de eliminar físicamente
      const dataToSend = {
        data: {
          id: deletingSku.id,
          sku: deletingSku.sku,
          sku_description: deletingSku.sku_description,
          sku_category: deletingSku.sku_category,
          manufacturing_time: deletingSku.manufacturing_time || 0,
          active: 0, // Marcar como inactivo
          user: 1 // TODO: Obtener del contexto de usuario actual
        }
      };
      
      const response = await putData('/api/admin/update_sku_main', dataToSend);
      
      // Verificar si la respuesta es un error
      if (response.response && response.response.status >= 400) {
        throw new Error(response.response.data?.msg || 'Error al desactivar');
      }
      
      // Actualización optimista: marcar como inactivo
      setSkus(prevSkus => prevSkus.map(sku => 
        sku.id === deletingSku.id ? { ...sku, active: 0 } : sku
      ));
      
      toastSuccess(`SKU "${deletingSku.sku_description}" desactivado exitosamente`);
      setShowDeleteModal(false);
      setDeletingSku(null);
    } catch (error) {
      console.error('Error desactivando SKU:', error);
      toastError(error.message || 'Error al desactivar el SKU');
    } finally {
      setLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setDeletingSku(null);
  };

  // Eliminación PERMANENTE (DELETE físico de la base de datos)
  const [showPermanentDeleteModal, setShowPermanentDeleteModal] = useState(false);
  const [permanentDeletingSku, setPermanentDeletingSku] = useState(null);

  const handlePermanentDelete = (sku) => {
    setPermanentDeletingSku(sku);
    setShowPermanentDeleteModal(true);
  };

  const confirmPermanentDelete = async () => {
    if (!permanentDeletingSku) return;
    
    try {
      setLoading(true);
      
      const dataToSend = {
        id: permanentDeletingSku.id,
        user: 1
      };
      
      const response = await deleteData('/api/admin/delete_sku_main', dataToSend);
      
      // Verificar si la respuesta es un error
      if (response?.response && response.response.status >= 400) {
        throw new Error(response.response.data?.msg || 'Error al eliminar');
      }
      
      // Actualización optimista: eliminar de la lista
      setSkus(prevSkus => prevSkus.filter(sku => sku.id !== permanentDeletingSku.id));
      
      toastSuccess(`SKU "${permanentDeletingSku.sku_description}" eliminado permanentemente`);
      setShowPermanentDeleteModal(false);
      setPermanentDeletingSku(null);
    } catch (error) {
      console.error('Error eliminando SKU:', error);
      toastError(error.message || 'Error al eliminar el SKU definitivamente');
    } finally {
      setLoading(false);
    }
  };

  const cancelPermanentDelete = () => {
    setShowPermanentDeleteModal(false);
    setPermanentDeletingSku(null);
  };

  // Filtrar SKUs según el toggle - Optimizado con useMemo
  const filteredSkus = useMemo(() => {
    return showInactive ? skus : skus.filter(sku => sku.active);
  }, [skus, showInactive]);

  return (
    <div className="sku-mains-manager">
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="card-title mb-0">
            <FaCube className="me-2 text-primary" />
            Gestión de SKUs Principales
          </h5>
          <div className="d-flex gap-2 align-items-center">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="showInactiveToggle"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="showInactiveToggle">
                Mostrar inactivos
              </label>
            </div>
            <button
              className="btn btn-primary"
              onClick={handleNew}
              disabled={loading}
            >
              <FaPlus className="me-2" />
              Nuevo SKU
            </button>
          </div>
        </div>

        <div className="card-body">
          {!showInactive && skus.length > filteredSkus.length && (
            <div className="alert alert-info d-flex justify-content-between align-items-center mb-3">
              <div>
                <i className="fas fa-info-circle me-2"></i>
                Hay <strong>{skus.length - filteredSkus.length}</strong> elemento(s) inactivo(s) oculto(s).
              </div>
              <button 
                className="btn btn-sm btn-outline-primary"
                onClick={() => setShowInactive(true)}
              >
                Mostrar todos
              </button>
            </div>
          )}

          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Cargando...</span>
              </div>
              <div className="mt-2 text-muted">Cargando SKUs...</div>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>SKU</th>
                    <th>Descripción</th>
                    <th>Categoría</th>
                    <th>Tiempo Fabricación</th>
                    <th>Estado</th>
                    <th className="text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSkus.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center text-muted py-4">
                        {showInactive ? 'No hay SKUs registrados' : 'No hay SKUs activos. Activa el toggle "Mostrar inactivos" para ver SKUs eliminados.'}
                      </td>
                    </tr>
                  ) : (
                    filteredSkus.map(sku => (
                      <tr key={sku.id}>
                        <td>#{sku.id}</td>
                        <td>
                          <code className="text-primary">{sku.sku}</code>
                        </td>
                        <td>
                          <strong>{sku.sku_description}</strong>
                        </td>
                        <td>
                          <span className="badge bg-secondary">
                            {sku.category_name}
                          </span>
                        </td>
                        <td>
                          <span className="badge bg-info">
                            {sku.manufacturing_time} días
                          </span>
                        </td>
                        <td>
                          <span className={`badge ${sku.active ? 'bg-success' : 'bg-danger'}`}>
                            {sku.active ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td className="text-center">
                          <div className="btn-group" role="group">
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handleEdit(sku)}
                              disabled={loading}
                              title="Editar SKU"
                            >
                              <FaEdit />
                            </button>
                            <button
                              className="btn btn-sm btn-outline-warning"
                              onClick={() => handleDelete(sku)}
                              disabled={loading}
                              title={sku.active ? "Inactivar SKU" : "Activar SKU"}
                            >
                              <FaTrash />
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handlePermanentDelete(sku)}
                              disabled={loading}
                              title="Eliminar definitivamente (irreversible)"
                            >
                              <FaExclamationTriangle />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal para crear/editar SKU */}
      {showModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingSku ? 'Editar SKU' : 'Nuevo SKU'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                  disabled={loading}
                ></button>
              </div>
              
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Categoría *</label>
                        <select
                          className="form-select"
                          name="sku_category"
                          value={formData.sku_category}
                          onChange={handleInputChange}
                          required
                          disabled={loading}
                        >
                          <option value="">Seleccionar categoría</option>
                          {categories.map(category => (
                            <option key={category.id} value={category.id}>
                              {category.category_name}
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      <div className="mb-3">
                        <label className="form-label">Descripción del SKU *</label>
                        <input
                          type="text"
                          className="form-control"
                          name="sku_description"
                          value={formData.sku_description}
                          onChange={handleInputChange}
                          required
                          disabled={loading}
                          placeholder="Ej: Respaldo 120x60 Tela Pana"
                        />
                      </div>
                    </div>
                    
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Tiempo de Fabricación (días) *</label>
                        <input
                          type="number"
                          className="form-control"
                          name="manufacturing_time"
                          value={formData.manufacturing_time}
                          onChange={handleInputChange}
                          required
                          disabled={loading}
                          min="0"
                          placeholder="5"
                        />
                      </div>
                      
                      <div className="mb-3">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            name="active"
                            checked={formData.active === 1}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              active: e.target.checked ? 1 : 0
                            }))}
                            disabled={loading}
                          />
                          <label className="form-check-label">
                            SKU activo
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                    disabled={loading}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading || !formData.sku_description.trim() || !formData.sku_category}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Guardando...
                      </>
                    ) : (
                      <>
                        <FaSave className="me-2" />
                        {editingSku ? 'Actualizar' : 'Crear'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de eliminación */}
      {showDeleteModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <FaExclamationTriangle className="text-warning me-2" />
                  Confirmar Eliminación
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={cancelDelete}
                  disabled={loading}
                ></button>
              </div>
              <div className="modal-body">
                <p>¿Estás seguro que deseas eliminar el SKU <strong>"{deletingSku?.sku_description}"</strong>?</p>
                <div className="alert alert-warning">
                  <FaExclamationTriangle className="me-2" />
                  El SKU será marcado como inactivo y no aparecerá en las opciones disponibles.
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={cancelDelete}
                  disabled={loading}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmDelete}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Eliminando...
                    </>
                  ) : (
                    <>
                      <FaTrash className="me-2" />
                      Eliminar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de eliminación PERMANENTE */}
      {showPermanentDeleteModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-danger">
              <div className="modal-header bg-danger text-white">
                <h5 className="modal-title">
                  <FaExclamationTriangle className="me-2" />
                  ⚠️ ELIMINAR DEFINITIVAMENTE
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={cancelPermanentDelete}
                  disabled={loading}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-danger">
                  <strong>⚠️ ADVERTENCIA: Esta acción es IRREVERSIBLE</strong>
                </div>
                <p>¿Está COMPLETAMENTE seguro que desea eliminar permanentemente el siguiente SKU?</p>
                {permanentDeletingSku && (
                  <div className="alert alert-warning">
                    <strong>SKU:</strong> {permanentDeletingSku.sku} - {permanentDeletingSku.sku_description}
                  </div>
                )}
                <ul className="text-danger">
                  <li>El SKU se eliminará <strong>permanentemente</strong> de la base de datos</li>
                  <li>NO se podrá recuperar esta información</li>
                  <li>Puede afectar pedidos y referencias existentes</li>
                </ul>
                <p className="mt-3">
                  <small className="text-muted">
                    💡 <strong>Recomendación:</strong> Si solo desea ocultar temporalmente este SKU,
                    use el botón de "Inactivar" en su lugar.
                  </small>
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={cancelPermanentDelete}
                  disabled={loading}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmPermanentDelete}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Eliminando...
                    </>
                  ) : (
                    <>
                      <FaExclamationTriangle className="me-2" />
                      ⚠️ SÍ, ELIMINAR DEFINITIVAMENTE
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SkuMainsManager;